import { useState, useEffect, useMemo } from 'react';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { toast } from 'sonner';
import { Trash2, Loader2, X, Check, Plus, Upload, FileText, Settings, Pencil, ArrowLeft, Sparkles, MessageCircleWarning, CirclePercent, Info, Calendar, TrendingUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/UI/tooltip"
import { useSelector } from 'react-redux';
import { Textarea } from '../../Components/UI/textarea';
import { saveMastersConfigAPI, manageProductAPI } from '../Settings/MasterDataAPI';
import { useCurrency } from '../../Hooks/useCurrency';
import BusinessInput from '../../Components/Common/BusinessInput';
import { Search } from 'lucide-react';
import { Checkbox } from "../../Components/UI/checkbox"
import { RadioGroup, RadioGroupItem } from "../../Components/UI/radio-group"
import { getActiveThreadTypesArr, checkIfInqOnlyMode } from "../../Store/Reducers";
import { CustomDropdown } from '../../Components/Common/DropDown';
import apiService from '../../Services/APIService';
import useIsMobile from '../../Hooks/useIsMobile';
import { AlertBox } from '../../Components/Common/AlertBox';
import ThrFieldConfig from './ThrFieldConfig/ThrFieldConfig';
import { isDayOfWeekType } from 'react-day-picker';
import { getThreadModeTitle, getThreadNameFromTypeID, thrStageShades } from '../../Utils/Utilities';
import { cn } from '../../Lib/Cn';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@radix-ui/react-hover-card';

// Predefined list of 20 colors
const ctgColors = ['#000000', '#0d1f2d', '#043d2a', '#0b5d1e', '#0c6980', '#bf5700', '#cd7f32', '#d4af37',
  '#6a0a0a', '#841617', '#8f5d46', '#434343', '#666666', '#999999', '#c0c0c0', '#CCCCCC'
];

// Master type configurations
const masterTypes = {
  rejectionReasons: {
    title: 'Rejection Reasons',
    subtitle: 'Rejection Reason',
    stateKey: 'rejectionReasons',
    successMessage: 'Rejection reason',
    isProduct: false,
    singularTitle: 'Reason',
    placeholder: 'Feature not available',
  },
  threadStages: {
    title: 'Thread Stages',
    subtitle: 'Thread Stage',
    stateKey: 'threadStages',
    successMessage: 'Thread Stage',
    isProduct: false,
    singularTitle: 'Thread Stage',
    placeholder: 'Thread Stages',
  },
  disqlReasons: {
    title: 'Disqualification Reasons',
    subtitle: 'Disqualification Reason',
    stateKey: 'disqlReasons',
    successMessage: 'Disqualification reason',
    isProduct: false,
    singularTitle: 'Reason',
    placeholder: 'Budget constraints',
  },
  segments: {
    title: 'Categories',
    subtitle: 'Categories',
    stateKey: 'segments',
    successMessage: 'Category',
    isProduct: false,
    singularTitle: 'Category',
    placeholder: 'Electronics',
    colors: ctgColors,
  },
  sources: {
    title: 'Sources',
    subtitle: 'Source',
    stateKey: 'sources',
    successMessage: 'Source',
    isProduct: false,
    singularTitle: 'Source',
    placeholder: 'Facebook',
  },
  taskTypes: {
    title: 'Task Types',
    subtitle: 'Task Types',
    stateKey: 'taskTypes',
    successMessage: 'Task type',
    isProduct: false,
    singularTitle: 'Task Type',
    placeholder: 'Call',
  },
  cities: {
    title: 'Cities',
    subtitle: 'City',
    stateKey: 'cities',
    successMessage: 'City',
    isProduct: false,
    singularTitle: 'City',
    placeholder: 'Vadodara',
  },
  connExtraFields: {
    title: 'Extra Fields (Contacts)',
    subtitle: 'Extra Field',
    stateKey: 'connExtraFields',
    successMessage: 'Extra Field',
    isProduct: false,
    singularTitle: 'Extra Field',
    placeholder: 'Task',
  },
  thrExtraFields: {
    title: 'Extra Fields (Threads)',
    subtitle: 'Extra Field',
    stateKey: 'thrExtraFields',
    successMessage: 'Extra Field',
    isProduct: false,
    singularTitle: 'Extra Field',
    placeholder: 'Task',
  },
  extraFields: {
    title: 'Extra Fields',
    subtitle: 'Extra Field',
    stateKey: 'connExtraFields',
    successMessage: 'Extra Field',
    isProduct: false,
    singularTitle: 'Extra Field',
    placeholder: 'Task',
  },
  // inactiveReasons: {
  //   title: 'Inactive Reasons',
  //   subtitle: 'Inactive Reason',
  //   stateKey: 'inactiveReasons',
  //   successMessage: 'Inactive reason',
  //   isProduct: false,
  //   singularTitle: 'Inactive Reason',
  //   placeholder: 'Not interested',
  // },
  products: {
    title: 'Offerings',
    subtitle: 'Offering',
    stateKey: 'products',
    successMessage: 'Offering',
    isProduct: true,
    singularTitle: 'Offering',
  },
  recordTypes: {
    title: 'Task/Record Types',
    subtitle: 'Record Type',
    stateKey: 'recordTypes',
    successMessage: 'Record Type',
    isProduct: false,
    singularTitle: 'Type',
    placeholder: 'Zoom Meeting',
  },
  regions: {
    title: 'Regions',
    subtitle: 'Region',
    stateKey: 'regions',
    successMessage: 'Region',
    isProduct: false,
    singularTitle: 'Region',
    placeholder: 'North-Gujarat',
  },
};

const MastersDialog = ({
  open,
  onClose,
  masterType = '',
  openEntry = false,
  directAdd = false,
  onProductAdded,
  prefilledName = '',
  parentDialogOpen = true,
  preselectedThreadTypeID = null,
}) => {
  const [hideFields, setHideFields] = useState({
    image: true,
    description: true,
    allowVisitors: true,
    models: false,
  });

  let isTypeField = masterType === "recordTypes" || masterType === "taskTypes";
  let isRecordType = masterType === "recordTypes";
  let isFailureReasons = masterType === "rejectionReasons";
  let isThrConfig = masterType === "threadStages";
  let isDisqlReasons = masterType === "disqlReasons";
  let isRegionField = masterType === "regions";
  let isSourceField = masterType === "sources";
  let isSegmentField = masterType === "segments";
  let isThrExtraField = masterType === "thrExtraFields";
  let isCitiesField = masterType === "cities";
  let isExtraFields = masterType === "extraFields";


  const [isOpen, setIsOpen] = useState(open);
  const { loading, error, ...masterData } = useSelector((state) => state.masterData);


  // Fix: Properly initialize based on field type
  // let initialValue = isTypeField ? { typ: prefilledName || '', isInxn: 0, hasGeo: 0 } : isSegmentField ? { CNm: prefilledName || '', Clr: ctgColors[0] } : (prefilledName || '');
  let initialValue = '';
  if (isTypeField) {
    initialValue = { typ: prefilledName || '', isInxn: 0, hasGeo: 0 };
  } else if (isSegmentField) {
    initialValue = { CNm: prefilledName || '', Clr: ctgColors[0] };
  } else {
    // This covers rejectionReasons, disqlReasons, and others
    initialValue = prefilledName || '';
  }

  if (isTypeField && openEntry) {
    initialValue.isInxn = isRecordType ? 1 : 0;
  }

  const [masterVal, setMasterVal] = useState(initialValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntryMode, setShowEntryMode] = useState(false);

  // New state for thread type selection
  const [productConfigOpen, setProductConfigOpen] = useState(false);
  const [configThreadType, setConfigThreadType] = useState(null);
  const [showThreadTypeSelection, setShowThreadTypeSelection] = useState(false);
  const [selectedThreadType, setSelectedThreadType] = useState({ tID: 0, threadType: '' });
  const [showFailureReasonEntry, setShowFailureReasonEntry] = useState(false);
  const [showThrExtraFieldEntry, setShowThrExtraFieldEntry] = useState(false);

  const isMobile = useIsMobile();
  const [thrEditModalOpen, setThrEditModalOpen] = useState(false);
  const [threadTarget, setThreadTarget] = useState({ name: '', sid: null, days: 1, chances: 10 });
  const [showThrConfigEntry, setShowThrConfigEntry] = useState(false);
  const [showStageTemplates, setShowStageTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('standardProduct');
  // Tab state for the combined Extra Fields dialog: 'contacts' | 'inquiries'
  const [extraFieldsTab, setExtraFieldsTab] = useState('contacts');

  // Stage templates for different thread types
  const stageTemplates = {
    standardProduct: [
      { SID: 1, S: "New Inquiry", D: 1 },
      { SID: 2, S: "Qualified", D: 2 },
      { SID: 3, S: "Presentation", D: 3 },
      { SID: 4, S: "Negotiation", D: 2 },
      { SID: 5, S: "Confirmation", D: 1 }
    ],
    customWork: [
      { SID: 1, S: "New Inquiry", D: 1 },
      { SID: 2, S: "Requirement Review", D: 1 },
      { SID: 3, S: "Proposal", D: 1 },
      { SID: 4, S: "Negotiation", D: 1 },
      { SID: 5, S: "Confirmation", D: 1 }
    ]
  };

  const [productForm, setProductForm] = useState({
    name: prefilledName || '',
    description: '',
    price: '',
    offeringType: 1, // 1 for Product, 2 for Service
    models: [],
    imageFile: null,
    imageName: '',
    allowInquiry: true,
    allowOrder: true,
    allowSupport: true
  });
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ index: null, pid: null, name: '', rid: null });
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [modelDialogValue, setModelDialogValue] = useState('');
  const [deleteModelAlertOpen, setDeleteModelAlertOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [editingProductID, setEditingProductID] = useState(null);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editingCategoryID, setEditingCategoryID] = useState(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const config = masterTypes[masterType] || {};
  const threadTypeArr = useSelector(getActiveThreadTypesArr);
  const currActiveThreadType = getThreadModeTitle(threadTypeArr);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  masterTypes.thrExtraFields.title = `Extra Fields (${currActiveThreadType.typPlr})`
  masterTypes.threadStages.title = `${currActiveThreadType.typSng} Stages`;
  masterTypes.threadStages.singularTitle = `${currActiveThreadType.typSng} Stage`;

  const masterListData = useMemo(() => {
    if (isTypeField) {
      return masterData['recordTypes']?.map(type => type.Typ) || [];
    } else if (isRegionField) {
      return masterData['regions']?.map(rgn => rgn.Rgn) || [];
    } else if (isThrConfig) {
      return masterData['threadStages'] || [];
    } else if (isThrExtraField) {
      return masterData['thrExtraFields'] || [];
    } else if (isExtraFields) {
      // For the combined dialog, switch based on active tab
      if (extraFieldsTab === 'inquiries') {
        return masterData['thrExtraFields'] || [];
      }
      return masterData['connExtraFields'] || [];
    } else if (isSourceField) {
      const sourceGroup = masterData['sources']?.find(src => src.TID === 1);
      return sourceGroup?.Srcs?.map(s => s.Src) || [];
    } else if (isSegmentField) {
      return masterData['segments']
        ?.filter(seg => seg.CID && seg.CNm)
        .map(seg => ({ CID: seg.CID, CNm: seg.CNm, Clr: `#${seg.Clr}` })) || [];
    } else if (isCitiesField) {
      return masterData['cities'] || [];
    }
    return masterData[config?.stateKey] || [];
  }, [masterData, config?.stateKey, isTypeField, isSegmentField, isThrConfig, isThrExtraField, isExtraFields, extraFieldsTab])

  useEffect(() => {
    if (preselectedThreadTypeID && threadTypeArr.length > 0) {
      const selectedType = threadTypeArr.find(type => type.TID === preselectedThreadTypeID);
      if (selectedType) {
        setSelectedThreadType({
          tID: selectedType.TID,
          threadType: selectedType.Typ
        });
      }
    }
  }, [preselectedThreadTypeID, threadTypeArr]);

  // Auto-select thread type in inquiry-only mode for thrExtraFields and failure reasons
  useEffect(() => {
    if (isOpen && (isThrExtraField || isFailureReasons || isDisqlReasons || isThrConfig) && isInqOnlyMode && threadTypeArr.length > 0) {
      const inqThread = threadTypeArr.find(type => type.TID === 1);
      if (inqThread) {
        setSelectedThreadType({
          tID: inqThread.TID,
          threadType: inqThread.Typ
        });
        setShowThreadTypeSelection(false); // Skip thread type selection in inqOnly mode
      }
    }
  }, [isOpen, isThrExtraField, isFailureReasons, isDisqlReasons, isThrConfig, isInqOnlyMode, threadTypeArr]);

  // Effect to handle directAdd prop for products and task types
  useEffect(() => {
    if (!open) return;

    // Handle products
    if (directAdd && config.isProduct) {
      setShowEntryMode(true);
      setShowMoreDetails(false);
      setProductForm(prev => ({ ...prev, name: prefilledName || '' }));
      return;
    }

    // Handle task types when directAdd is true
    if (directAdd && masterType === 'taskTypes') {
      setShowEntryMode(true);
      if (prefilledName) {
        setMasterVal(isTypeField ? { typ: prefilledName, isInxn: 0, hasGeo: 0 } : prefilledName);
      }
      return;
    }

    if (directAdd && (isFailureReasons || isDisqlReasons)) {
      if (preselectedThreadTypeID) {
        setShowFailureReasonEntry(true);
        setShowThreadTypeSelection(false);
        setShowEntryMode(false);
        console.log(prefilledName)
        setMasterVal(prefilledName || '');
      }
      return;
    }

    // Handle thread extra fields when directAdd is true
    if (directAdd && isThrExtraField) {
      if (preselectedThreadTypeID) {
        setShowThrExtraFieldEntry(true);
        setShowThreadTypeSelection(false);
        setShowEntryMode(false);
        setMasterVal(prefilledName || '');
      }
      return;
    }

    // Handle failure reasons, disqualification reasons, and thread extra fields
    if (openEntry && !config.isProduct && (isFailureReasons || isDisqlReasons || isThrExtraField)) {
      if (preselectedThreadTypeID) {
        if (isFailureReasons || isDisqlReasons) {
          setShowFailureReasonEntry(true);
        } else if (isThrExtraField) {
          setShowThrExtraFieldEntry(true);
        }
        setShowThreadTypeSelection(false);
        setShowEntryMode(false);
      } else {
        setShowThreadTypeSelection(true);
        setShowEntryMode(false);
        setShowFailureReasonEntry(false);
        setShowThrExtraFieldEntry(false);
      }
      return;
    }

    // Handle other simple masters
    if (openEntry && !config.isProduct) {
      setShowEntryMode(true);
      if (prefilledName) {
        setMasterVal(isTypeField ? { typ: prefilledName, isInxn: 0, hasGeo: 0 } : isSegmentField ? { CNm: prefilledName, Clr: ctgColors[0] } : prefilledName);
      }
    }

    if (threadTypeArr.length === 1) {
      setSelectedThreadType({ tID: threadTypeArr[0].TID, threadType: threadTypeArr[0].Typ });
    }
  }, [isOpen, openEntry, directAdd, config.isProduct, isFailureReasons, isDisqlReasons, isThrExtraField, prefilledName, preselectedThreadTypeID, masterType]);

  // Effect to handle failure reasons flow
  useEffect(() => {
    if (isOpen && (isFailureReasons || isDisqlReasons || isThrExtraField) &&
      !showEntryMode && !showFailureReasonEntry && !openEntry && !showThrExtraFieldEntry) {
      setShowThreadTypeSelection(true);
    }
    // For extraFields inquiries tab, also show thread type selection
    if (isOpen && isExtraFields && extraFieldsTab === 'inquiries' &&
      !showEntryMode && !showThrExtraFieldEntry && !openEntry) {
      setShowThreadTypeSelection(true);
    }
  }, [isOpen, isFailureReasons, isDisqlReasons, isThrExtraField, isExtraFields, extraFieldsTab, showEntryMode, showFailureReasonEntry, showThrExtraFieldEntry, openEntry]);


  //Effect to handle thread configurations flow
  useEffect(() => {
    if (isOpen && isThrConfig && !showEntryMode && !showThrConfigEntry) {
      setShowThreadTypeSelection(true);
    }
  }, [isOpen, isThrConfig, showEntryMode, showThrConfigEntry]);

  useEffect(() => {
    if (!isOpen || !showEntryMode || !config.isProduct) {
      setShowMoreDetails(false);
    }
  }, [isOpen, showEntryMode, config.isProduct]);

  const filteredSimpleList = masterListData?.filter(item => {
    // For disqualification reasons, skip filtering as we handle it differently
    if (isDisqlReasons) {
      return true;
    }

    return isSegmentField ?
      String(item.CNm || '').toLowerCase().includes(searchTerm.trim().toLowerCase()) :
      String(item || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
  });

  const filteredProductList = masterListData?.filter(item =>
    String(item?.name || item?.PNm || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  // Handle to open failure reason entry model
  const handleAddFailureReasonClick = () => {
    if (selectedThreadType.tID == 0) {
      toast.error('Please select a thread type first.');
      return;
    }
    setShowThreadTypeSelection(false);
    setShowFailureReasonEntry(true);
  };

  const handleAddThrExtraFieldClick = () => {
    if (selectedThreadType.tID == 0) {
      toast.error('Please select a thread type first.');
      return;
    }
    setShowThreadTypeSelection(false);
    setShowThrExtraFieldEntry(true);
  };


  const handleFailuareReasonSave = async (actionType, deleteID = 0) => {
    let rejectionReasonsArr = JSON.parse(masterListData.find(rsns => rsns.TID == selectedThreadType.tID)?.Rsns || '[]');
    let rejectionParam = '';
    let addedItemName = "";

    if (actionType == 'add') {
      if (!masterVal?.trim()) {
        toast.error(`Please enter a ${config.singularTitle.toLowerCase()}.`);
        return;
      }

      if (rejectionReasonsArr.some((rsns) => rsns?.R.trim().toLowerCase() == masterVal.trim().toLowerCase())) {
        toast.error(`This ${config.singularTitle.toLowerCase()} already exists.`, 'error');
        return;
      }

      let maxRID = rejectionReasonsArr.length != 0 ? rejectionReasonsArr.reduce((rsns, curr) => {
        if (rsns.RID > curr) {
          return rsns.RID;
        }
        return curr;
      }, 0).RID : 0;

      let newReason = { RID: maxRID + 1, R: masterVal };
      rejectionReasonsArr.push(newReason);

      // KEY CHANGE: Different handling for disqlReasons
      rejectionParam = {
        tID: selectedThreadType.tID,
        rsns: isDisqlReasons ? [newReason] : rejectionReasonsArr
      };

      addedItemName = masterVal.trim();
    }
    else if (actionType == 'delete') {
      let reasonToDelete = rejectionReasonsArr.find(rsns => rsns.RID == deleteID);
      let delRejectionReasons = rejectionReasonsArr.filter(rsns => rsns.RID != deleteID);

      // KEY CHANGE: Different handling for disqlReasons
      rejectionParam = {
        tID: selectedThreadType.tID,
        rsns: isDisqlReasons ? [reasonToDelete] : delRejectionReasons
      };
    }

    try {
      if (rejectionParam == '') {
        toast.error(`Failed to ${actionType} ${config.successMessage.toLowerCase()}.`, 'error');
        return;
      }

      const apiResponse = await saveMastersConfigAPI(config.stateKey, actionType, rejectionParam);


      let successMsg = '';
      if (actionType == 'add') {
        successMsg = `${config.successMessage} added.`;
        setMasterVal('');

        if (onProductAdded && addedItemName) {
          onProductAdded(addedItemName);
        }

        // KEY CHANGE: Go back to thread type selection
        if (directAdd) {
          handleClose();
        } else {

          setShowFailureReasonEntry(false);
          setShowThreadTypeSelection(true);
        }
      }
      else if (actionType == 'delete') {
        successMsg = `${config.successMessage} deleted.`;
        setMasterVal('');

        // Force re-render by briefly hiding and showing the list
        setShowThreadTypeSelection(false);
        setTimeout(() => {
          setShowThreadTypeSelection(true);
        }, 0);
      }

      // toast.success(successMsg);
      setMasterVal(initialValue);
      setShowEntryMode(false);
      setShowFailureReasonEntry(false);
      setDeleteAlertOpen(false);

    }
    catch (error) {
      toast.error(error.message || `Failed to ${actionType} ${config.successMessage.toLowerCase()}`);
    }
  };

  // Handle to edit thread stage updation
  const handleEditThreadStage = async () => {
    // Validate stage name
    if (!threadTarget.name.trim()) {
      toast.error('Please enter a stage name.');
      return;
    }
    if (!threadTarget.name.trim().length > 25) {
      toast.error('Please enter a stage name within 25 characters.');
      return;
    }

    // Validate days
    if (threadTarget.days > 99) {
      toast.error('Closing days must be less than or equal to 99.');
      return;
    }

    if (threadTarget.chances > 100) {
      toast.error('Chances percentage must be less than or equal to 100.')
    }

    // Check for duplicate stage name within the same thread (case-insensitive)
    const foundStagesObj = masterListData?.find(
      (t) => t.TID === selectedThreadType.tID
    );

    if (!foundStagesObj) {
      toast.error('Thread type not found.');
      return;
    }

    // Find and set current stage
    const foundStage = foundStagesObj.Stages.find(
      stage => stage.SID === threadTarget.sid
    );

    if (!foundStage) {
      toast.error('Stage not found.');
      return;
    }

    // Check if both name and days are unchanged
    if (foundStage.S.trim() === threadTarget.name.trim() && foundStage.D === parseInt(threadTarget.days) && foundStage.C === parseInt(threadTarget.chances)) {
      toast.error('Please modify the stage name, estimated days or percentage chances.');
      return;
    }

    if (foundStagesObj) {
      const isDuplicate = foundStagesObj.Stages.some(stage => {
        const isSameStage = stage.SID === threadTarget.sid;
        return !isSameStage && stage.S.toLowerCase() === threadTarget.name.trim().toLowerCase();
      });

      if (isDuplicate) {
        toast.error('Another stage with the same name already exists.');
        return;
      }
    }

    try {
      const stageData = {
        tID: selectedThreadType.tID,
        sID: threadTarget.sid,
        stageName: threadTarget.name.trim(),
        days: parseInt(threadTarget.days),
        chances: parseInt(threadTarget.chances)
      };

      await saveMastersConfigAPI('threadStages', 'update', stageData);
      // toast.success(`${currActiveThreadType.typSng} stage updated.`);

      // Close modal and reset
      setThrEditModalOpen(false);
      setThreadTarget({
        name: '',
        sid: null,
        days: 1,
        chances: 10
      });

    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update stage.');
    }
  };

  // Handler for bulk stage update
  const handleBulkUpdateStages = async () => {
    try {
      const stageData = {
        tID: selectedThreadType.tID,
        stages: stageTemplates[selectedTemplate]
      };

      await saveMastersConfigAPI('threadStages', 'bulkUpdate', stageData);
      // toast.success(`${getThreadNameFromTypeID(selectedThreadType.tID, threadTypeArr)} stages updated.`);

      // Refresh the data
      setShowStageTemplates(false);

    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update stages.');
    }
  };

  const handleThreadExtraFieldSave = async (actionType, deleteID = 0) => {
    let threadExtraFields = masterListData.find(fields => fields.TID == selectedThreadType.tID) || [];
    let fieldParam = '';
    let addedItemName = "";

    if (threadExtraFields.length != 0) {
      threadExtraFields = JSON.parse(threadExtraFields?.Fields).filter(field => !field.IsD) || []
    }

    if (actionType == 'add') {
      // Check if the value is empty
      if (!masterVal?.trim()) {
        toast.error(`Please enter a ${config.singularTitle.toLowerCase()}.`);
        return;
      }

      // Check for duplicates
      if (threadExtraFields.length != 0 && threadExtraFields.some((field) => field?.F.trim().toLowerCase() == masterVal.trim().toLowerCase())) {
        toast.error(`This thread extra field already exists.`);
        return;
      }

      if (threadExtraFields.length >= 5) {
        toast.error(`You cannot add more than five fields.`);
        return;
      }

      let maxFID = threadExtraFields.length != 0 ? threadExtraFields.reduce((field, curr) => {
        if (field.FID > curr.FID) {
          return field;
        }
        return curr;
      }, threadExtraFields[0]).FID : 0;

      fieldParam = { TID: selectedThreadType.tID, FID: maxFID + 1, F: masterVal };

      addedItemName = masterVal.trim();
    }
    else if (actionType == 'delete') {
      threadExtraFields.forEach(field => {
        if (field.FID == deleteID) {
          field.IsD = true;
        }
      });
      fieldParam = { TID: selectedThreadType.tID, FID: deleteID, F: "" };
    }

    try {
      if (fieldParam == '') {
        toast.error(`Failed to ${actionType} ${config.successMessage.toLowerCase()}.`);
        return;
      }
      const thrFieldsStateKey = isExtraFields ? 'thrExtraFields' : config.stateKey;
      await saveMastersConfigAPI(thrFieldsStateKey, actionType, fieldParam);

      let successMsg = '';
      if (actionType == 'add') {
        successMsg = `Extra field added.`;
        setMasterVal('');

        if (onProductAdded && addedItemName) {
          onProductAdded(addedItemName);
        }
      }
      else if (actionType == 'delete') {
        successMsg = `Extra field deleted.`;
        setMasterVal('');
      }

      // toast.success(successMsg);
      setMasterVal(initialValue);
      setShowEntryMode(false);
      setShowThrExtraFieldEntry(false);
      setDeleteAlertOpen(false);
    }
    catch (error) {
      toast.error(error.message || `Failed to ${actionType} ${config.successMessage.toLowerCase()}`);
    }
  };

  const handleMasterConfigSave = async (actionType, value) => {
    let successMsg = '';
    let sanatizedMasterVal = "";
    let addedItemName = ""; // Add this to track the added item name

    if (actionType == 'add') {
      sanatizedMasterVal = isTypeField ? masterVal : isSegmentField ? masterVal : masterVal.trim();;

      // Check if the value is empty and show toast
      if (!sanatizedMasterVal || (isTypeField && !sanatizedMasterVal.typ?.trim()) || (isSegmentField && !sanatizedMasterVal.CNm?.trim())) {
        toast.error(`Please enter a ${config.singularTitle.toLowerCase()}.`);
        return;
      }

      // Check for duplicate name (case-insensitive)
      if (!isTypeField && !isSegmentField && masterListData && masterListData.some((item) => item.toLowerCase() === sanatizedMasterVal.trim().toLowerCase())) {
        toast.error(`This ${config.singularTitle.toLowerCase()} already exists.`, 'error');
        return;
      }
      else if (isTypeField && masterListData && masterListData.some((item) => item.toLowerCase() === sanatizedMasterVal.typ.trim().toLowerCase())) {
        toast.error(`This ${config.singularTitle.toLowerCase()} already exists.`, 'error');
        return;
      }
      else if (isSegmentField && masterListData && masterListData.some((item) => item.CNm.toLowerCase() === sanatizedMasterVal.CNm.trim().toLowerCase())) {
        toast.error(`This ${config.singularTitle.toLowerCase()} already exists.`, 'error');
        return;
      }

      // Store the added item name for callback
      addedItemName = isTypeField ? sanatizedMasterVal.typ.trim() : isSegmentField ? sanatizedMasterVal.CNm.trim() : sanatizedMasterVal.trim();
    } else if (actionType == 'update') {
      if (!masterVal.CNm?.trim()) {
        toast.error(`Please enter a ${config.singularTitle.toLowerCase()}.`);
        return;
      } else if (masterListData && masterListData.some((item) =>
        item.CID !== editingCategoryID && item.CNm.toLowerCase() === masterVal.CNm.trim().toLowerCase())) {
        toast.error(`Another ${config.singularTitle.toLowerCase()} with this name already exists.`);
        return;
      }

      sanatizedMasterVal = {
        CID: editingCategoryID,
        CNm: masterVal.CNm.trim(),
        Clr: masterVal.Clr.replace('#', '').trim()
      };
    } else if (actionType == 'delete') {
      // Functionality for delete the master config.
      sanatizedMasterVal = isSegmentField ? value : value.trim();
    }

    try {
      // For failure reasons, include the selected thread type
      let apiData = sanatizedMasterVal;

      await saveMastersConfigAPI(config.stateKey, actionType, apiData);

      if (actionType == 'add') {
        successMsg = `${config.successMessage} added.`;
        setMasterVal('');

        // Call the callback with the added item name
        if (onProductAdded && addedItemName) {
          onProductAdded(addedItemName);
        }
        // handleClose();
      }
      else if (actionType == 'update') {
        successMsg = `${config.successMessage} updated.`;
        setIsEditingCategory(false);
        setEditingCategoryID(null);
      }
      else if (actionType == 'delete') {
        successMsg = `${config.successMessage} deleted.`;
        setMasterVal('');
      }
      // toast.success(successMsg);
      setMasterVal(isSegmentField ? { CNm: '', Clr: ctgColors[0] } : isTypeField ? { typ: '', isInxn: 0, hasGeo: 0 } : '');
      setMasterVal(initialValue);

      if (actionType == 'add' && directAdd && onProductAdded && addedItemName) {
        setShowEntryMode(false);
        setShowFailureReasonEntry(false);
        setShowThrExtraFieldEntry(false);
        return;
      }

      setShowEntryMode(false);
      setShowFailureReasonEntry(false);
      setShowThrExtraFieldEntry(false);
    } catch (error) {
      toast.error(error.message || `Failed to add ${config.successMessage.toLowerCase()}`);
    }
  };

  const handleProductInputChange = (e) => {
    const { name, value } = e.target;
    setProductForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleAddProduct = async () => {
    // Validate offering type
    if (!productForm.offeringType) {
      toast.error('Please select an offering type (Product or Service).');
      return;
    }

    if (!productForm.name.trim()) {
      toast.error('Please enter an offering name.');
      return;
    }
    // Check for duplicate product name (case-insensitive)
    if (
      masterListData &&
      masterListData.some((item) => {
        const isSameProduct = isEditingProduct && (item.PID === editingProductID);
        return !isSameProduct && (item.name || item.PNm).toLowerCase() === productForm.name.trim().toLowerCase();
      })
    ) {
      toast.error(`This ${config.singularTitle} already exists.`, 'error');
      return;
    }

    // Validate models/packages for both product and service types
    if (productForm.models.length > 0) {
      const hasEmptyModel = productForm.models.some(m => !m.MNm.trim());
      if (hasEmptyModel) {
        const fieldName = productForm.offeringType === 1 ? 'model' : 'package';
        toast.error(`Please fill in all ${fieldName} names or remove empty ${fieldName}s.`);
        return;
      }
    }

    // Check maximum limit
    if (productForm.models.length > 10) {
      const fieldName = productForm.offeringType === 1 ? 'models' : 'packages';
      toast.error(`You can add a maximum of 10 ${fieldName}.`);
      return;
    }

    try {
      let imageUrl = '';

      // Handle image upload if file is selected
      if (productForm.imageFile) {
        try {
          const uploadResult = await apiService.uploadFile(
            productForm.imageFile,
            "Products",
            "both"
          );

          if (uploadResult.error) {
            toast.error(uploadResult.message);
            return;
          }
          imageUrl = uploadResult.data.relativePath;
        } catch (uploadError) {
          console.error('Image upload error:', uploadError);
          toast.error('Failed to upload image file');
          return;
        }
      }

      // Build Acpt string based on checkboxes
      let acptString = '';
      if (productForm.allowInquiry) acptString += 'I';
      if (productForm.allowOrder) acptString += 'O';
      if (productForm.allowSupport) acptString += 'S';

      const newProductData = {
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        price: parseFloat(productForm.price.trim()) || 0,
        offeringType: productForm.offeringType,
        models: productForm.models.filter(m => m.MNm.trim()),
        imageUrl: imageUrl || productForm.imageName,
        acpt: acptString,
        allowInquiry: productForm.allowInquiry,
        allowOrder: productForm.allowOrder,
        allowSupport: productForm.allowSupport
      };

      // Add product ID if editing
      if (isEditingProduct && editingProductID) {
        newProductData.pid = editingProductID;
      }

      const apiAction = isEditingProduct ? 'update' : 'add';
      await manageProductAPI(apiAction, newProductData);
      // toast.success(`Offering ${isEditingProduct ? 'updated' : 'added'}.`);
      const productName = newProductData.name;
      setProductForm({
        name: '',
        description: '',
        price: '',
        offeringType: 1,
        models: [],
        imageFile: null,
        imageName: '',
        allowInquiry: true,
        allowOrder: true,
        allowSupport: true
      });

      if (directAdd) {
        if (onProductAdded) {
          onProductAdded(productName);
        }
        handleClose();
      } else {
        setShowEntryMode(false);
        setIsEditingProduct(false);
        setEditingProductID(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add offering.', 'error');
    }
  };
  const handleDeleteProduct = async () => {
    if (!deleteTarget.pid) {
      toast.error('Invalid offering ID.', 'error');
      setDeleteAlertOpen(false);
      setDeleteTarget({ index: null, pid: null, name: '' });
      return;
    }
    try {
      await manageProductAPI('delete', deleteTarget.pid);
      // toast.success(`Offering deleted.`);
      setDeleteAlertOpen(false);
      setDeleteTarget({ index: null, pid: null, name: '' });
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to delete offering due to an error',
        'error'
      );
      setDeleteAlertOpen(false);
      setDeleteTarget({ index: null, pid: null, name: '' });
    }
  };

  const handleClose = () => {
    onClose();
    // Fix: Reset to proper initial value based on field type
    setMasterVal(isSegmentField ? { CNm: '', Clr: ctgColors[0] } : isTypeField ? { typ: '', isInxn: 0, hasGeo: 0 } : '');
    setShowEntryMode(false);
    setShowThreadTypeSelection(false);
    setShowFailureReasonEntry(false);
    setShowThrExtraFieldEntry(false);
    setProductForm({
      name: '',
      description: '',
      price: '',
      offeringType: 1,
      models: [],
      imageFile: null,
      imageName: '',
      allowInquiry: true,
      allowOrder: true,
      allowSupport: true
    });
    setDeleteAlertOpen(false);
    setDeleteTarget({ index: null, pid: null, name: '', rid: null });
    setIsEditingProduct(false);
    setEditingProductID(null);
    setIsEditingCategory(false);
    setEditingCategoryID(null);
    setShowMoreDetails(false);
  };

  // Watch for parent dialog closing and close master dialog if opened via directAdd
  useEffect(() => {
    if (directAdd && !parentDialogOpen && isOpen) {
      handleClose();
    }
  }, [parentDialogOpen, directAdd, isOpen]);

  const handleDeleteModel = () => {
    if (modelToDelete) {
      const newModels = productForm.models.filter((_, i) => i !== modelToDelete.index);
      setProductForm(prev => ({ ...prev, models: newModels }));
      setModelDialogOpen(false);
      setEditingModel(null);
      setModelDialogValue('');
      setDeleteModelAlertOpen(false);
      setModelToDelete(null);
      // toast.success(`${productForm.offeringType === 1 ? 'Model' : 'Package'} deleted.`);
    }
  };
  // New function to close only the entry form, not the entire dialog
  const handleCloseEntryForm = () => {
    // If opened via directAdd (from ThreadEntry), close the entire dialog
    if (directAdd) {
      handleClose();
      return;
    }

    // Otherwise, just close the entry form and show the list
    setShowEntryMode(false);
    setShowFailureReasonEntry(false);
    setShowThrConfigEntry(false);
    setShowThrExtraFieldEntry(false);
    setProductForm({
      name: '',
      description: '',
      price: '',
      offeringType: 1,
      models: [],
      imageFile: null,
      imageName: '',
      allowInquiry: true,
      allowOrder: true,
      allowSupport: true
    });
    setDeleteTarget({ index: null, pid: null, name: '', rid: null, fid: null });
    setThreadTarget({ name: '', sid: null, days: 0, chances: 0 }); // Updated this line
    setIsEditingProduct(false);
    setEditingProductID(null);
    setIsEditingCategory(false);
    setEditingCategoryID(null);

    // ADD THIS: Close edit modal and show thread type selection for thread stages
    if (thrEditModalOpen) {
      setThrEditModalOpen(false);
      setShowThreadTypeSelection(true);
    }
  };

  // Prevent modal close on outside click
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      onClose();
    }
  };

  // Render thread type selecter dialog
  const threadTypeSelectionWrapper = () => {
    let filteredThreadTypes = isDisqlReasons
      ? threadTypeArr?.filter(item => item.Flow === 'In')
      : threadTypeArr;

    // Sales Orders doesn't have configurable stages - keep it out of the Thread Stages type dropdown
    if (isThrConfig) {
      filteredThreadTypes = filteredThreadTypes?.filter(
        (tType) => !/^sales\s+orders?$/i.test((tType.Typ || '').trim())
      );
    }

    let threadOptions = filteredThreadTypes?.map(tType => {
      return { label: tType.Typ, value: tType.Typ }
    });

    return (
      <div className="p-0">
        <div className="px-4">
          <div className={`${threadTypeArr.length > 1 && 'pt-1'} space-y-6`}>
            <div className="text-center">
              <div className="space-y-2">
                {threadTypeArr.length > 1 && !thrEditModalOpen && (
                  <CustomDropdown
                    fieldID={'threadType'}
                    required={false}
                    label={''}
                    placeholder={'Select Type'}
                    options={threadOptions}
                    value={threadTypeArr.find((type) => type.TID == selectedThreadType.tID)?.Typ}
                    onChange={(val) => {
                      let selectedType = threadTypeArr.find((type) => type.Typ == val);
                      setSelectedThreadType({ tID: selectedType.TID, threadType: selectedType.Typ });
                      setShowStageTemplates(false)
                    }}
                    emptyMessage={<>Please add types in settings.</>}
                  />
                )}

                {renderThreadTypeDataList(isThrConfig, isDisqlReasons)}

                {!isExtraFields && !thrEditModalOpen && (<div className="flex sm:flex-row gap-3 py-4">
                  {(selectedThreadType.tID != 0) && !isThrConfig && (
                    <Button
                      onClick={(isFailureReasons || isDisqlReasons) ? handleAddFailureReasonClick : handleAddThrExtraFieldClick}
                      className={`sahi-primary-button sm:flex-none sm:min-w-[140px]`}
                    >
                      <Plus className="h-4 w-4" />
                      Add {config.singularTitle}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className={`sahi-secondary-button sm:flex-none sm:min-w-[100px]`}
                  >
                    <X className="w-3 h-3" />
                    Close
                  </Button>
                </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Replace both renderThreadTypeDataList and renderThrStagesConfigList with this:
  const renderThreadTypeDataList = (isThreadStage = false) => {
    const selectedData = masterListData?.find(t => t.TID === selectedThreadType.tID);

    const isThrExtraFieldOrInquiriesTab = isThrExtraField || (isExtraFields && extraFieldsTab === 'inquiries');

    const getItemName = (item) => isThreadStage ? item.S : isThrExtraFieldOrInquiriesTab ? item.F : item.R;
    const getItemID = (item) => isThreadStage ? item.SID : isThrExtraFieldOrInquiriesTab ? item.FID : item.RID;

    // Get appropriate data array - use key to force re-render
    let dataList;
    if (isThreadStage) {
      dataList = selectedData?.Stages ? [...selectedData.Stages].sort((a, b) => Number(a.SID) - Number(b.SID)) : [];
    } else if (isThrExtraFieldOrInquiriesTab) {
      const fieldsStr = selectedData?.Fields || '[]';
      const fields = JSON.parse(fieldsStr).filter(field => !field.IsD);
      dataList = [...fields].sort((a, b) => getItemName(a).localeCompare(getItemName(b)));
    } else {
      const rsns = JSON.parse(selectedData?.Rsns || '[]');
      dataList = [...rsns].sort((a, b) => getItemName(a).localeCompare(getItemName(b)));


    }
    return (
      <div className="p-0">
        {/* Stage Template Button - Only show for Thread Stages and not in edit mode */}
        {/* {selectedData?.TID == 1 && isThreadStage && !thrEditModalOpen && !showStageTemplates && (
          <div className="px-4 pt-2">
            <Button
              onClick={() => setShowStageTemplates(!showStageTemplates)}
              variant="outline"
              className="w-full border-2 border-dashed border-gray-300 hover:border-sahi-blue"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Use Stage Template
            </Button>
          </div>
        )} */}

        {/* Stage Template Preview and Update */}
        {showStageTemplates && isThreadStage && !thrEditModalOpen && (
          <div className="">
            <div className="border-2 sahi-radius p-4 space-y-3 bg-sahi-disable">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-sahi-primary-color" />
                  Stage Templates
                </h4>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowStageTemplates(false)}
                  className="h-7 w-7 hover:bg-sahi-blue-light"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Template Selection Dropdown */}
              <div className="space-y-2">
                <CustomDropdown
                  fieldID={'templateType'}
                  required={false}
                  label={''}
                  placeholder={'Select Template'}
                  options={[
                    { label: 'Standard Product/Services', value: 'standardProduct' },
                    { label: 'Custom Work', value: 'customWork' }
                  ]}
                  value={selectedTemplate}
                  onChange={(val) => {
                    setSelectedTemplate(val);
                  }}
                />
              </div>

              <div className="text-xs bg-sahi-orange-light sahi-radius p-2 flex items-center gap-2">
                <MessageCircleWarning className="w-4 h-4 text-sahi-orange flex-shrink-0" />
                <span>All stages will be overwritten for {getThreadNameFromTypeID(selectedThreadType.tID, threadTypeArr, "Typ")}.</span>
              </div>

              {/* Template Preview */}
              <div className="space-y-2 sahi-radius px-3">
                {stageTemplates[selectedTemplate].map((stage, idx) => (
                  <div key={idx} className="text-sm text-gray-700 flex items-center gap-3 py-1">
                    <span className="w-6 h-6 bg-sahi-dark-blue text-sahi-white rounded-full flex items-center justify-center text-xs font-semibold shadow-sm">
                      {stage.SID}
                    </span>
                    <span className="font-medium">{stage.S}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleBulkUpdateStages}
                className="w-full sahi-primary-button"
              >
                <Check className="h-4 w-4 mr-2" strokeWidth={3} />
                Update All Stages
              </Button>
            </div>
          </div>
        )}

        {/* Existing Data List */}
        {!showStageTemplates && <div className={`overflow-auto lg:max-h-[60vh] flex flex-col custom-scroll ${(!thrEditModalOpen) && 'pt-0'}`}>
          {loading ? (
            <div className="py-6 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : error ? (
            <div className="py-6 text-center">
              <p className="text-sahi-red text-sm">{error}</p>
            </div>
          ) : thrEditModalOpen ? (
            // Edit form for thread stage
            <div className="p-0">
              <div className="px-0">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="masterValInput" className="text-sm font-medium text-gray-700">
                      {config.singularTitle} Name
                    </Label>
                    <Input
                      value={threadTarget.name}
                      onChange={e => setThreadTarget(prev => ({ ...prev, name: e.target.value }))}
                      maxLength={25}
                      placeholder={`e.g. Negotiation`}
                      className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 autofocus"
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <div className={cn("flex gap-1.5", isMobile ? "flex-col items-start" : "flex-row items-center")}>
                      <Label htmlFor="estimatedDays" className="text-sm font-medium text-gray-700">
                        Time to Close
                      </Label>
                      {isMobile ?
                        <p className="text-xs text-gray-600 text-left">
                          Number of days for which an inquiry is in this stage typically
                        </p> : <HoverCard openDelay={200} closeDelay={100}>
                          <HoverCardTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center"
                            >
                              <span className="info-icon">i</span>
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side="right"
                            align="start"
                            sideOffset={4}
                            className={`p-3 bg-white border border-gray-200 text-gray-900 sahi-radius max-w-100`}
                          >
                            <div className="text-left">
                              <div className="flex gap-2 items-center text-sahi-green"><Calendar className="h-4 w-4 text-sahi-green" /><h1 className="text-sm font-semibold">Time to Close</h1></div>
                              <p className="text-xs text-gray-600 text-left border-t border-gray-300 mt-2 pt-2">
                                Number of days for which<br />an inquiry is in this stage typically
                              </p>
                            </div>
                          </HoverCardContent>
                        </HoverCard>}
                    </div>
                    <div className='flex justify-start items-center gap-2'>
                      <Input
                        id="estimatedDays"
                        value={threadTarget.days}
                        maxLength={3}
                        onFocus={(e) => e.target.select()}
                        onChange={e => {
                          const value = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                          if (value === '' || value <= 200) {
                            setThreadTarget(prev => ({ ...prev, days: value }));
                          }
                        }}
                        placeholder="e.g. 5"
                        className="h-11 text-sm text-right border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 w-20 d-inline-block"
                      />
                      <p className='text-sm'>{threadTarget.days > 1 ? 'Days' : 'Day'}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className={cn("flex gap-1.5", isMobile ? "flex-col items-start" : "flex-row items-center")}>
                      <Label htmlFor="estimatedChances" className="text-sm font-medium text-gray-700">
                        Sales Chances
                      </Label>
                      {isMobile ?
                        <p className="text-xs text-gray-600 text-left">
                          Chances of an inquiry in this stage getting converted successfully
                        </p> :
                        <HoverCard openDelay={200} closeDelay={100}>
                          <HoverCardTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center"
                            >
                              <span className="info-icon">i</span>
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side="right"
                            align="start"
                            sideOffset={4}
                            className={`p-3 bg-white border border-gray-200 text-gray-900 sahi-radius`}
                          >
                            <div className="text-left">
                              <div className="flex gap-2 items-center text-sahi-green"><TrendingUp className="h-4 w-4 text-sahi-green" /><h1 className="text-sm font-semibold">Sales Chances</h1></div>
                              <p className="text-xs text-gray-600 text-left border-t border-gray-300 mt-2 pt-2">
                                Chances of an inquiry in this stage<br />getting converted successfully
                              </p>
                            </div>

                          </HoverCardContent>

                        </HoverCard>}
                    </div>
                    <div className='flex justify-start items-center gap-2'>
                      <Input
                        id="estimatedChances"
                        value={threadTarget.chances}
                        maxLength={3}
                        onFocus={(e) => e.target.select()}
                        onChange={e => {
                          const value = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                          if (value === '' || value <= 100) {
                            setThreadTarget(prev => ({ ...prev, chances: value }));
                          }
                        }}
                        placeholder="e.g. 75"
                        className="h-11 text-sm text-right border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 w-20 d-inline-block"
                      />
                      <p className='text-sm'>%</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full flex gap-4 sm:flex-row justify-start sm:gap-3 py-4">
                <Button onClick={handleEditThreadStage} className="w-auto">
                  <Check className="h-4 w-4" strokeWidth={3} />
                  Update Stage
                </Button>

                <Button
                  variant="outline"
                  onClick={() => {
                    setThrEditModalOpen(false);
                    setThreadTarget({ name: '', sid: null, days: 0, chances: 0 });
                  }}
                  className={`sahi-secondary-button sm:flex-none sm:min-w-[100px]`}
                >
                  <X className="w-3 h-3" />
                  Close
                </Button>
              </div>
            </div>
          ) : (dataList && dataList.length > 0) ? (
            <div className="space-y-4 custom-scroll">
              {dataList.map((item, index) => (
                <div
                  key={`${selectedThreadType.tID}-${getItemID(item) || index}`}
                  className={`flex items-center justify-between bg-white shadow-sm border border-gray-200 sahi-radius px-4 py-2 w-full ${isThreadStage ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (isThreadStage) {
                      setThreadTarget({ name: item.S, sid: item.SID, days: item.D || 0, chances: item.C || 0 });
                      setThrEditModalOpen(true);
                    }
                  }}
                  style={{
                    borderLeftWidth: isThreadStage ? 6 : 1,
                    borderLeftStyle: "solid",
                    borderLeftColor: isThreadStage
                      ? (isInqOnlyMode
                        ? ["#062f12", "#094418", "#0b5d1e", "#107a28", "#17a53b"][item.SID - 1]
                        : thrStageShades(item.SID - 1, selectedThreadType.tID)
                      )
                      : ""
                  }}
                >
                  <div className="flex flex-col items-start justify-start flex-1 gap-2 min-w-0">
                    <span className={`text-sm text-left text-text-color break-all py-2`}>
                      {getItemName(item)}
                    </span>

                    {isThreadStage && (item.D !== undefined && item.D !== null || item.C !== undefined && item.C !== null) && (
                      <div className="flex items-start justify-start gap-2 flex-shrink-0">
                        {item.D !== undefined && item.D !== null && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md border">
                            <Calendar className="h-3.5 w-3.5 text-gray-700" />
                            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-700 font-medium`}>{item.D} {[0, 1].includes(item.D) ? 'day' : 'days'}</span>
                          </div>
                        )}

                        {item.C !== undefined && item.C !== null && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-md border border-sahi-green-light">

                            <TrendingUp className="h-3.5 w-3.5 text-sahi-green" />
                            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-sahi-green font-medium`}>{item.C}% {item.C > 1 ? 'chances' : 'chance'}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {(isThreadStage ||
                    (!isFailureReasons && !isDisqlReasons) ||
                    ((isFailureReasons || isDisqlReasons) && dataList.length > 1)) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isThreadStage) {
                            setThreadTarget({ name: item.S, sid: item.SID, days: item.D || 0, chances: item.C || 0 });
                            setThrEditModalOpen(true);
                          } else if (isThrExtraField || (isExtraFields && extraFieldsTab === 'inquiries')) {
                            setDeleteTarget({
                              index: null,
                              pid: null,
                              name: item.F,
                              rid: null,
                              fid: item.FID
                            });
                            setDeleteAlertOpen(true);
                          } else {
                            setDeleteTarget({
                              index: null,
                              pid: null,
                              name: item.R,
                              rid: item.RID,
                              fid: null
                            });
                            setDeleteAlertOpen(true);
                          }
                        }}
                        className={isThreadStage
                          ? "h-8 w-8 p-0 flex-shrink-0 ml-2"
                          : "text-sahi-red hover:bg-sahi-red-light h-8 w-8 flex-shrink-0 ml-2"
                        }
                      >
                        {isThreadStage ? (
                          <Pencil className="h-4 w-4 text-sahi-blue" />
                        ) : (
                          ((!isFailureReasons && !isDisqlReasons) ||
                            ((isFailureReasons || isDisqlReasons) && dataList.length > 1)) &&
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                </div>
              ))}
            </div>
          ) : (
            <div className="w-fit text-sm text-sub-text-color lg:whitespace-nowrap bg-sahi-white sahi-radius px-5 py-2 border">
              No {isThreadStage ? 'thread stages' : (isDisqlReasons ? 'disqualification reasons' : (isThrExtraField || (isExtraFields && extraFieldsTab === 'inquiries')) ? 'extra fields' : 'failure reasons')} found.
            </div>
          )}
        </div>}
      </div>
    );
  };

  const renderProductFormFields = () => (
    <div className="space-y-4">
      <div className={`${!hideFields.image && (showMoreDetails || !isInqOnlyMode) ? "grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-6" : ""}`}>
        {/* Left Side - Basic fields always shown */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label required={true}>Type</Label>
            <RadioGroup
              value={String(productForm.offeringType)}
              onValueChange={(value) => {
                setProductForm(prev => ({ ...prev, offeringType: parseInt(value) }));
              }}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="product-type" />
                <Label htmlFor="product-type" className="cursor-pointer font-normal">
                  Product
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="2" id="service-type" />
                <Label htmlFor="service-type" className="cursor-pointer font-normal">
                  Service
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" required={true}>
              {productForm.offeringType == 1 ? "Product Offering Name" : "Service Offering Name"}
            </Label>
            <Input
              id="name"
              name="name"
              value={productForm.name}
              onChange={handleProductInputChange}
              placeholder={productForm.offeringType == 1 ? "e.g. Laptop" : "e.g. Consultation"}
              className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 w-full"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        {/* Right Side - Photo section - shown only in more details or non-inquiry mode */}
        {!hideFields.image && (showMoreDetails || !isInqOnlyMode) && (
          <div className="space-y-2">
            {/* <Label htmlFor="product-image" className="text-sm font-medium text-gray-700">
            Image
          </Label> */}
            {isMobile === true ? (
              <div className="relative">
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
                      if (allowedTypes.includes(file.type)) {
                        setProductForm(prev => ({ ...prev, imageFile: file, imageName: file.name }));
                      } else {
                        toast.error("Please select a valid image format (JPEG, PNG, WebP)");
                        e.target.value = "";
                      }
                    }
                  }}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left h-11 px-3 sahi-input text-sm!"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProductForm(prev => ({ ...prev, imageFile: null, imageName: "" }));
                    document.getElementById("product-image").value = "";
                  }}
                >
                  {productForm.imageName || productForm.imageFile ? (
                    <div className="flex items-center gap-2 truncate w-full">
                      <FileText className={`h-4 w-4 text-sahi-blue flex-shrink-0`} />
                      <span className="truncate text-sm flex-1">{productForm.imageName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 flex-shrink-0 hover:bg-gray-100 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProductForm(prev => ({ ...prev, imageFile: null, imageName: "" }));
                          document.getElementById("product-image").value = "";
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sub-text-color">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">Choose File</span>
                    </div>
                  )}
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="product-image"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
                      if (allowedTypes.includes(file.type)) {
                        setProductForm(prev => ({ ...prev, imageFile: file, imageName: file.name }));
                      } else {
                        toast.error("Please select a valid image format (JPEG, PNG, WebP)");
                        e.target.value = "";
                      }
                    }
                  }}
                  className="hidden"
                />
                {productForm.imageName || productForm.imageFile ? (
                  <div className="relative w-full h-[150px] border-2 border-gray-300 rounded-md overflow-hidden group">
                    <img
                      src={`https://sahicrm.com/Uploads/${productForm.imageName}`}
                      alt="Product preview"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0 bg-white/80 hover:bg-white rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProductForm(prev => ({ ...prev, imageFile: null, imageName: "" }));
                        document.getElementById("product-image").value = "";
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full !bg-white h-[150px] flex items-center justify-center border-gray-300 hover:border-gray-400 transition-all duration-200 p-2"
                    onClick={() => document.getElementById("product-image").click()}
                  >
                    <div className="w-full h-full border-[1px] border-dashed border-gray-300 flex flex-col items-center justify-center">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sub-text-color font-normal text-[12px]">Upload Photo</span>
                    </div>
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Models/Packages Section - Show only in more details or non-inquiry mode */}
      {/* {(showMoreDetails || !isInqOnlyMode) && ( */}
      <div className={`!mt-1 ${productForm.models.length != 0 ? 'space-y-2' : ''}`}>
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700 mt-4">
            {productForm.offeringType === 1 ? 'Models' : 'Packages'}
          </Label>
        </div>
        <span className="text-xs italic text-gray-600 my-6">
          Enter up 10 offering (product / service) {productForm.offeringType === 1 ? 'categories' : 'categories'}
        </span>
        <div className={`space-y-2`}>
          {productForm.models.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {productForm.models.map((model, index) => (
                <div key={model.MID} className="flex items-center gap-2 bg-white shadow-sm border border-gray-200 sahi-radius px-4 py-2">
                  <span className="flex-1 text-sm text-text-color break-all">

                    {model.MNm}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingModel({ index, ...model });
                      setModelDialogValue(model.MNm);
                      setModelDialogOpen(true);
                    }}
                    className="h-8 w-8 text-sahi-blue hover:bg-sahi-blue-light flex-shrink-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {productForm.models.length < 10 && (
            <button
              type="button"
              onClick={() => {
                setEditingModel(null);
                setModelDialogValue('');
                setModelDialogOpen(true);
              }}
              className="text-sm text-sahi-primary-color hover:text-foreground underline decoration-1 underline-offset-4 transition-colors"
            >
              + Add {productForm.offeringType === 1 ? 'Model' : 'Package'} ({productForm.models.length}/10)
            </button>
          )}
        </div>
      </div>
      {/* )} */}

      {/* Description - Show only in more details or non-inquiry mode */}
      {!hideFields.description && (showMoreDetails || !isInqOnlyMode) && (
        <div className="space-y-2 !mt-[30px]">
          <Label htmlFor="description" className="text-sm font-medium text-gray-700">
            Description
          </Label>
          <Textarea
            id="description"
            name="description"
            value={productForm.description}
            onChange={handleProductInputChange}
            placeholder="e.g. Fast and efficient laptop."
            className="min-h-[100px] text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 rounded-md w-full px-3 py-2 resize-none"
            autoComplete="off"
            maxLength={4000}
          />
        </div>
      )}

      {/* Allow Options - Show only in more details or non-inquiry mode */}
      {!hideFields.allowVisitors && (showMoreDetails || !isInqOnlyMode) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-gray-700">Allow visitors to enter</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="info-icon cursor-help">i</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Allow visitors to create these types of threads</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex flex-wrap gap-4">
            {threadTypeArr.filter(t => t.Flow === 'In').map(threadType => {
              const getCheckboxProps = () => {
                switch (threadType.TID) {
                  case 1:
                    return {
                      id: "allow-inquiry",
                      checked: productForm.allowInquiry,
                      onCheckedChange: (checked) => setProductForm(prev => ({ ...prev, allowInquiry: checked })),
                      label: "Inquiry",
                      showSettings: productForm.allowInquiry
                    };
                  case 2:
                    return {
                      id: "allow-order",
                      checked: productForm.allowOrder,
                      onCheckedChange: (checked) => setProductForm(prev => ({ ...prev, allowOrder: checked })),
                      label: "Sale",
                      showSettings: productForm.allowOrder
                    };
                  case 3:
                    return {
                      id: "allow-support",
                      checked: productForm.allowSupport,
                      onCheckedChange: (checked) => setProductForm(prev => ({ ...prev, allowSupport: checked })),
                      label: "Support Ticket",
                      showSettings: productForm.allowSupport
                    };
                  default:
                    return null;
                }
              };

              const checkboxProps = getCheckboxProps();
              if (!checkboxProps) return null;

              return (
                <div key={threadType.TID} className="flex items-center space-x-2">
                  <Checkbox
                    id={checkboxProps.id}
                    checked={checkboxProps.checked}
                    onCheckedChange={checkboxProps.onCheckedChange}
                    className="border-gray-300"
                  />
                  <Label htmlFor={checkboxProps.id} className="cursor-pointer font-normal">
                    {checkboxProps.label}
                  </Label>
                  {checkboxProps.showSettings && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0 ml-1"
                      onClick={() => {
                        setConfigThreadType(threadType.TID);
                        setProductConfigOpen(true);
                      }}
                    >
                      <Settings className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
              );
            })
            }
          </div>
        </div>
      )}
    </div>
  );

  // Render product list
  const renderProductList = () => (
    <div className="p-0">
      <div className="overflow-auto px-4 lg:max-h-[60vh] flex flex-col custom-scroll">
        {loading ? (
          <div className="py-6 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="py-6 text-center">
            <p className="text-sahi-red text-sm">{error}</p>
          </div>
        ) : filteredProductList && filteredProductList.length > 0 ? (
          <div className="space-y-4 custom-scroll">
            {[...filteredProductList]
              .sort((a, b) => (a.name || a.PNm).localeCompare(b.name || b.PNm))
              .map((item, index) => (
                <div
                  key={item.PID || index}
                  className="flex items-center justify-between bg-white shadow-sm border border-gray-200 sahi-radius px-4 py-2 w-full"
                >
                  <span className="flex-1 text-sm text-text-color break-all py-2">
                    {item.name || item.PNm}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-sahi-blue hover:bg-sahi-blue-light"
                      onClick={() => {
                        // Parse models if they come as JSON string
                        let parsedModels = [];
                        try {
                          const modelsData = item.Mdls || item.mdls || item.Models || item.models;
                          if (modelsData) {
                            parsedModels = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                          }
                        } catch (error) {
                          console.error('Error parsing models:', error);
                          parsedModels = [];
                        }

                        // Ensure models have the correct structure
                        const formattedModels = Array.isArray(parsedModels) ? parsedModels.map((model, idx) => ({
                          MID: model.mID || model.MID || idx + 1,
                          MNm: model.mNm || model.MNm || model.name || ''
                        })) : [];

                        // Parse Acpt string (e.g., "IOS" means all three are checked)
                        const acptString = item.Acpt || item.acpt || '';
                        const allowInquiry = acptString.includes('I');
                        const allowOrder = acptString.includes('O');
                        const allowSupport = acptString.includes('S');

                        // Get offering type (PTID: 1 = Product, 2 = Service)
                        const offeringType = item.PTID || item.ptid || item.offeringType || item.OTyp || 1;

                        // Get image URL
                        const imageUrl = item.Img || item.img || item.imageUrl || item.ImgURL || '';

                        // Get price/rate
                        const price = item.Rate || item.rate || item.price || item.Price || '';

                        // Get description
                        const description = item.D || item.d || item.description || item.Desc || '';

                        setShowEntryMode(true);
                        setShowMoreDetails(false);
                        setProductForm({
                          name: item.PNm || item.name || '',
                          description: description,
                          price: price.toString(),
                          offeringType: offeringType,
                          models: formattedModels,
                          imageFile: null,
                          imageName: imageUrl,
                          allowInquiry: allowInquiry,
                          allowOrder: allowOrder,
                          allowSupport: allowSupport
                        });
                        // Store the product ID for editing
                        setIsEditingProduct(true);
                        setEditingProductID(item.PID);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                        onClick={() => {
                          setDeleteTarget({ index: null, pid: item.PID, name: item.name || item.PNm, rid: null });
                          setDeleteAlertOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>

                    </>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="w-fit text-sm text-sub-text-color lg:whitespace-nowrap bg-sahi-white sahi-radius px-5 py-2 border">
            No {config.singularTitle}s found.
          </div>
        )}
      </div>
    </div>
  );

  // Render simple master data list
  const renderSimpleMasterList = () => (
    <div className="p-0 sm:flex-1 sm:min-h-0 sm:flex sm:flex-col">
      <div className="overflow-auto px-4 lg:max-h-[60vh] sm:flex-1 sm:min-h-0 flex flex-col custom-scroll">
        {loading ? (
          <div className="py-6 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="py-6 text-center">
            <p className="text-sahi-red text-sm">{error}</p>
          </div>
        ) : filteredSimpleList && filteredSimpleList.length > 0 && !isFailureReasons && !isDisqlReasons && !isThrExtraField ? (
          <div className="space-y-4">
            {[...filteredSimpleList]
              .sort((a, b) => (isSegmentField ? a.CNm : a).localeCompare(isSegmentField ? b.CNm : b))
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white shadow-sm border border-gray-200 sahi-radius px-4 py-2 w-full"
                >
                  <div className="flex items-center gap-2">
                    {isSegmentField && (
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.Clr }}
                      />
                    )}
                    <span className="flex-1 text-sm text-text-color break-all py-2"> {isSegmentField ? item.CNm : item} </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {isSegmentField && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setShowEntryMode(true);
                          setIsEditingCategory(true);
                          setEditingCategoryID(item.CID);
                          setMasterVal({ CNm: item.CNm, Clr: item.Clr });
                        }}
                        className="h-8 w-8 text-sahi-blue hover:bg-sahi-blue-light"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {(() => {
                      const isProtectedType = isRecordType && (item === "Call" || item === "Message");
                      return (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (!isProtectedType) {
                              setDeleteTarget({ index, pid: null, name: isSegmentField ? item.CNm : item, rid: null, segment: isSegmentField ? item : null });
                              setDeleteAlertOpen(true);
                            }
                          }}
                          className={`h-8 w-8 sahi-radius transition-colors ${isProtectedType ? 'text-gray-400 cursor-not-allowed opacity-50 hover:bg-gray-100 hover:text-gray-400' : 'text-sahi-red hover:bg-sahi-red-light'}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="w-fit text-sm text-sub-text-color lg:whitespace-nowrap bg-sahi-white sahi-radius px-5 py-2 border">
            No {
              (config.stateKey === "connExtraFields"
                ? config.title.replace(/\s*\(.*?\)/, "")   // Remove "(Contacts)"
                : config.title
              ).toLowerCase()
            } found.
          </div>
        )}
      </div>
    </div>
  );

  // Checkbox component
  const getCheckBox = (fieldID, label, isPadding = true) => {
    const id = fieldID == -1 ? 'checkBox' : fieldID;
    return <>
      <div className={`flex items-center space-x-2 py-1`}>
        <Checkbox
          id={id}
          checked={masterVal[fieldID]}
          onCheckedChange={(checked) => setMasterVal((prev) => ({ ...prev, [fieldID]: checked }))}
          className="border-gray-300"
        />
        <Label htmlFor={id} className={"cursor-pointer"}>
          {label}
        </Label>
      </div>
    </>
  }

  const isSearchBarNeeded = () => {
    if (masterType == 'connExtraFields' || masterType === 'extraFields' || masterType == "thrExtraFields" || masterType == 'recordTypes' || masterType == 'inactiveReasons' || masterType == 'rejectionReasons'
      || masterType == 'disqlReasons' || masterType == 'threadStages') {
      return false;
    }
    return masterListData && masterListData.length >= 10;
  }

  // Render simple master entry form
  const renderSimpleMasterEntry = () => (
    <div className="p-0">
      <div className="px-4">
        <div className="pt-1 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="masterValInput" className="text-sm font-medium text-gray-700">
              {isDisqlReasons
                ? `Disqualification ${config.singularTitle} for ${selectedThreadType.threadType}`
                : isThrExtraField
                  ? `Extra field entry for ${selectedThreadType.threadType}`
                  : config.singularTitle}
            </Label>
            <Input
              key={`${showFailureReasonEntry}-${showThrExtraFieldEntry}-${prefilledName}`}
              id="masterValInput"
              placeholder={`e.g. ${config.placeholder}`}
              value={isTypeField ? masterVal.typ : isSegmentField ? masterVal.CNm : masterVal}
              onChange={(e) =>
                isTypeField
                  ? setMasterVal((prev) => ({ ...prev, typ: e.target.value }))
                  : isSegmentField ? setMasterVal((prev) => ({ ...prev, CNm: e.target.value }))
                    : setMasterVal(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === ',' && !isRegionField && !isThrExtraField) {
                  e.preventDefault();
                }
              }}
              className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 autofocus"
              autoComplete="off"
              maxLength={isRegionField ? 50 : undefined}
              autoFocus
            />
            {isSegmentField && (
              <div className="space-y-2 !mt-4">
                <Label className="text-sm font-medium text-gray-700">Display Color</Label>
                <div className="space-y-2">
                  {/* First row - colors 0-9 */}
                  <div className="flex flex-wrap gap-2">
                    {ctgColors.map((color, index) => (
                      <button
                        key={index}
                        type="button"
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none',
                          masterVal.Clr === color
                            ? 'ring-2 ring-offset-2 ring-sahi-primary-color'
                            : 'border border-gray-300 hover:border-sahi-primary-color/50'
                        )}
                        style={{ backgroundColor: color }}
                        onClick={() => setMasterVal((prev) => ({ ...prev, Clr: color }))}
                        title={color}
                      >
                        {masterVal.Clr === color && (
                          <Check className="h-4 w-4 text-white" strokeWidth={3} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className='flex-col !mt-[15px] sm:flex-row sm:mt-0 gap-2 !sm:gap-[15px]'>
              {isTypeField && (!isRecordType || !openEntry) ? getCheckBox("isInxn", "Mark as Interaction Type") : ''}
              {isTypeField && (isRecordType || !openEntry) ? getCheckBox("hasGeo", "Ask for Geo Tag (visit verification)") : ''}
            </div>
          </div>
        </div>
      </div>
      {!isExtraFields && (
        <div className="py-4 px-4">
          <div className="w-full flex gap-4 sm:flex-row justify-start sm:gap-3">
            <Button
              onClick={() => {
                if (showFailureReasonEntry) {
                  handleFailuareReasonSave('add');
                } else if (showThrExtraFieldEntry) {
                  handleThreadExtraFieldSave('add');
                } else {
                  handleMasterConfigSave((isSegmentField && isEditingCategory) ? 'update' : 'add');
                }
              }}
              className="w-auto"
            >
              <Check className="h-4 w-4" strokeWidth={3} />
              {loading
                ? ((isSegmentField && isEditingCategory) ? 'Updating...' : 'Adding...')
                : ((isSegmentField && isEditingCategory) ? `Update ${config.singularTitle}` : `Save ${config.singularTitle}`)
              }
            </Button>
            <Button
              variant="outline"
              onClick={handleCloseEntryForm}
              className={`sahi-secondary-button sm:flex-none sm:min-w-[100px]`}
            >
              <X className="w-3 h-3" />
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Render product entry form
  const renderProductEntry = () => (
    <div className="p-0">
      <div className="px-4 max-h-[82vh] overflow-y-auto custom-scroll">
        <div className="space-y-4">{renderProductFormFields()}</div>
        {/* {isInqOnlyMode && !showMoreDetails && (
          <Button
            variant="outline"
            onClick={() => setShowMoreDetails(true)}
            className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[140px] mt-4"
          >
            <Plus className="h-4 w-4  " />
            More Details
          </Button>
        )} */}
      </div>
      <div className="p-4">
        <div className="w-full flex gap-4 sm:flex-row justify-start sm:gap-3">
          <Button onClick={handleAddProduct} className="w-auto">
            <Check className="h-4 w-4" strokeWidth={3} />
            {loading ? (isEditingProduct ? 'Updating...' : 'Adding...') : (isEditingProduct ? `Update ${config.singularTitle}` : `Save ${config.singularTitle}`)}
          </Button>
          <Button
            variant="outline"
            onClick={handleCloseEntryForm}
            className={`sahi-secondary-button sm:flex-none sm:min-w-[100px]`}
          >
            <X className="w-3 h-3" />
            Close
          </Button>
        </div>
      </div>
    </div>
  );

  // Extra Fields tab toggle UI (Contacts / Inquiries)
  const renderExtraFieldsToggle = (isInline = false) => {
    const toggleButtons = (
      <div className={cn(
        "bg-zinc-200/80 sm:bg-[#f4f4f5] rounded-md sm:rounded-[5px] items-center border border-zinc-300/70 sm:border-gray-200",
        isInline ? "flex w-fit p-1 h-10" : "grid grid-cols-2 p-0.5 w-full"
      )}>
        <button
          type="button"
          onClick={() => {
            setExtraFieldsTab('contacts');
            setShowThrExtraFieldEntry(false);
            setShowEntryMode(false);
            setShowThreadTypeSelection(false);
            setSelectedThreadType({ tID: 0, threadType: '' });
          }}
          className={cn(
            "text-sm font-normal rounded flex items-center justify-center text-center",
            isInline ? "h-full w-20 sm:rounded-[4px]" : "py-1.5 px-3.5",
            extraFieldsTab === 'contacts'
              ? (isInline ? "bg-white text-black shadow-xs" : "bg-sahi-white text-zinc-900 shadow-xs border border-zinc-200/80")
              : (isInline ? "text-gray-500 hover:text-black hover:bg-transparent" : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/50 border border-transparent")
          )}
        >
          Contacts
        </button>
        <button
          type="button"
          onClick={() => {
            setExtraFieldsTab('inquiries');
            setShowEntryMode(false);
            setShowThrExtraFieldEntry(false);
            setShowThreadTypeSelection(true);
            setSelectedThreadType({ tID: 1, threadType: 'INQ' });
          }}
          className={cn(
            "text-sm font-normal rounded flex items-center justify-center text-center",
            isInline ? "h-full w-20 sm:rounded-[4px]" : "py-1.5 px-3.5",
            extraFieldsTab === 'inquiries'
              ? (isInline ? "bg-white text-black shadow-xs" : "bg-sahi-white text-zinc-900 shadow-xs border border-zinc-200/80")
              : (isInline ? "text-gray-500 hover:text-black hover:bg-transparent" : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/50 border border-transparent")
          )}
        >
          {currActiveThreadType?.typPlr || 'Inquiries'}
        </button>
      </div>
    );

    if (isInline) {
      return toggleButtons;
    }

    return (
      <div className="px-4 pb-2 w-full">
        {toggleButtons}
      </div>
    );
  };

  // Main content renderer
  const renderContent = () => {
    if (isExtraFields) {
      if (extraFieldsTab === 'contacts') {
        // Contacts tab: show simple list of connExtraFields
        if (showThrExtraFieldEntry) {
          // Reuse the simple master entry form (uses masterVal + handleMasterConfigSave for connExtraFields)
          return renderSimpleMasterEntry();
        }
        return renderSimpleMasterList();
      } else {
        // Inquiries tab: behave like isThrExtraField
        if (showThrExtraFieldEntry) {
          return renderSimpleMasterEntry();
        }
        return threadTypeSelectionWrapper();
      }
    }
    if ((isFailureReasons || isDisqlReasons) && showThreadTypeSelection) {
      return threadTypeSelectionWrapper();
    }
    if ((isThrExtraField && !showThrExtraFieldEntry)) {
      return threadTypeSelectionWrapper();
    }
    if (showEntryMode || showFailureReasonEntry || showThrExtraFieldEntry) {
      return config.isProduct ? renderProductEntry() : renderSimpleMasterEntry();
    }
    if (isThrConfig) {
      return threadTypeSelectionWrapper();
    }
    return config.isProduct ? renderProductList() : renderSimpleMasterList();
  };

  // Get dialog title
  const getDialogTitle = () => {
    if (isExtraFields) {
      if (showThrExtraFieldEntry) return `New Extra Field`;
      return 'Extra Fields';
    }
    if (selectedThreadType.tID != 0 && (showFailureReasonEntry || showThrExtraFieldEntry)) {
      return `New ${config.singularTitle}`;
    }
    if (showEntryMode || showFailureReasonEntry || showThrExtraFieldEntry) {
      return isEditingProduct ? `Edit ${config.singularTitle}` : `${config.singularTitle} Entry`;
    }
    if (thrEditModalOpen) {
      return `Edit ${currActiveThreadType.typSng} Stage`
    }
    return config.title;
  };

  // Get dialog max width
  const getDialogMaxWidth = () => {
    // if (isTypeField) {
    //   return 'w-full sm:min-w-[500px]';
    // }
    if (isDisqlReasons) {
      return 'w-full sm:max-w-lg';
    }
    return 'sm:max-w-md w-full';
  }

  const handleShowEntryForm = () => {
    if (isFailureReasons || isDisqlReasons || isThrExtraField) {
      setShowThreadTypeSelection(true);
    } else {
      setShowEntryMode(true);

      if (config.isProduct) {
        setShowMoreDetails(false);
        setProductForm(prev => ({ ...prev, name: searchTerm }));
      } else if (isTypeField) {
        setMasterVal(prev => ({ ...prev, typ: searchTerm }));
      } else if (isSegmentField) {
        setMasterVal(prev => ({ ...prev, name: searchTerm }));
      } else {
        setMasterVal(searchTerm);
      }
    }
  };

  if (!masterType || !masterTypes[masterType]) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={`${config.isProduct && showEntryMode ? 'sm:max-w-xl' : getDialogMaxWidth()}`}>
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="sm:hidden flex items-center justify-between w-full h-8">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </DialogClose> */}
                <DialogClose
                  tabIndex={-1}
                  className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                  onClick={(e) => {
                    e.preventDefault();
                    if (thrEditModalOpen) {
                      setThrEditModalOpen(false);
                      setThreadTarget({ name: '', sid: null, days: 0, chances: 0 });
                      // Stay on thread type selection, don't close dialog
                    } else if (showEntryMode || showFailureReasonEntry || showThrExtraFieldEntry) {
                      handleCloseEntryForm();
                    } else {
                      handleClose();
                    }
                  }}
                >
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </DialogClose>
                <DialogTitle className="text-lg font-medium">
                  <div className="truncate">
                    {getDialogTitle()}
                  </div>
                </DialogTitle>
              </div>
              {/* Right Side */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isExtraFields && !showEntryMode && !isFailureReasons && !isDisqlReasons && !isThrExtraField && !showThreadTypeSelection && !isThrConfig && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleShowEntryForm}
                    className="h-9 w-9 p-0  hover:bg-white/20 flex items-center justify-center"
                  >
                    <Plus className='plus-icon' />
                  </Button>
                )}

                {showEntryMode && config.isProduct && isEditingProduct && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setDeleteTarget({ index: null, pid: editingProductID, name: productForm.name, rid: null });
                      setDeleteAlertOpen(true);
                    }}
                    className="h-9 w-9 p-0  hover:bg-white/20 flex items-center justify-center"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
              {getDialogTitle()}
            </DialogTitle>
            <div className="hidden sm:flex items-center gap-2">
              {isExtraFields && !showThrExtraFieldEntry && !showEntryMode && (
                renderExtraFieldsToggle(true)
              )}
              {showEntryMode && config.isProduct && isEditingProduct && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setDeleteTarget({ index: null, pid: editingProductID, name: productForm.name, rid: null });
                    setDeleteAlertOpen(true);
                  }}
                  className="h-9 w-9 p-0 text-white hover:bg-white/20 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 p-0  sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
                tabIndex={-1}
                onClick={() => {
                  if (thrEditModalOpen) {
                    setThrEditModalOpen(false);
                    setThreadTarget({ name: '', sid: null, days: 0, chances: 0 });
                    // Don't close the entire dialog
                  } else if (showEntryMode || showFailureReasonEntry || showThrExtraFieldEntry) {
                    handleCloseEntryForm();
                  } else {
                    handleClose();
                  }
                }}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DialogHeader>
          {/* Extra Fields tab toggle shown on mobile view below header */}
          {isExtraFields && !showThrExtraFieldEntry && !showEntryMode && (
            <div className="sm:hidden">
              {renderExtraFieldsToggle(false)}
            </div>
          )}

          {!showEntryMode && !isFailureReasons && !isThrExtraField && !showThreadTypeSelection && isSearchBarNeeded() && !(masterListData.length == 0) &&
            <div className='px-4 py-1'>
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={`Search ${config.singularTitle}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                />
              </div>
            </div>
          }

          {renderContent()}

          {/* Bottom action buttons */}
          {isExtraFields && !showEntryMode && (
            <div className="flex sm:flex-row gap-3 px-4 pb-4">
              <Button
                onClick={() => {
                  if (!showThrExtraFieldEntry) {
                    if (extraFieldsTab === 'contacts') {
                      setShowThrExtraFieldEntry(true);
                    } else {
                      handleAddThrExtraFieldClick();
                    }
                  } else {
                    if (extraFieldsTab === 'contacts') {
                      handleMasterConfigSave('add');
                    } else {
                      handleThreadExtraFieldSave('add');
                    }
                  }
                }}
                className="sahi-primary-button sm:flex-none sm:min-w-[140px]"
              >
                {showThrExtraFieldEntry ? (
                  <>
                    <Check className="h-4 w-4" strokeWidth={3} />
                    Save Extra Field
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add Extra Field
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (showThrExtraFieldEntry) {
                    setShowThrExtraFieldEntry(false);
                    setMasterVal('');
                    if (extraFieldsTab === 'inquiries') {
                      setShowThreadTypeSelection(true);
                    }
                  } else {
                    handleClose();
                  }
                }}
                className="sahi-secondary-button sm:flex-none sm:min-w-[100px]"
              >
                <X className="w-3 h-3" />
                Close
              </Button>
            </div>
          )}

          {!isExtraFields && !showEntryMode && !isFailureReasons && !isDisqlReasons && !isThrExtraField && !showThreadTypeSelection && !isThrConfig &&
            <div className="flex sm:flex-row gap-3 px-4 pb-4">
              <Button
                onClick={
                  selectedThreadType.tID != 0 && (isFailureReasons || isDisqlReasons)
                    ? handleAddFailureReasonClick
                    : isThrExtraField
                      ? handleAddThrExtraFieldClick
                      : handleShowEntryForm
                }
                className={`sahi-primary-button sm:flex-none sm:min-w-[140px]`}
              >
                <Plus className="h-4 w-4" />
                Add {config.singularTitle}
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className={`sahi-secondary-button sm:flex-none sm:min-w-[100px]`}
              >
                <X className="w-3 h-3" />
                Close
              </Button>
            </div>
          }
        </DialogContent>
      </Dialog>
      {/* Model/Package Edit Dialog */}
      <Dialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
        <DialogContent className="sm:max-w-md w-full">
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="sm:hidden flex items-center justify-between w-full h-8">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </DialogClose>
                <DialogTitle className="text-lg font-medium">
                  <div className="truncate">
                    {editingModel ? `Edit ${productForm.offeringType === 1 ? 'Model' : 'Package'}` : `${productForm.offeringType === 1 ? 'Model' : 'Package'} Entry`}
                  </div>
                </DialogTitle>
              </div>
              {/* Right Side */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {editingModel && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                    onClick={() => {
                      setModelToDelete({ index: editingModel.index, name: modelDialogValue });
                      setDeleteModelAlertOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
              {editingModel ? `Edit ${productForm.offeringType === 1 ? 'Model' : 'Package'}` : `${productForm.offeringType === 1 ? 'Model' : 'Package'} Entry`}
            </DialogTitle>
            <div className="hidden sm:flex items-center gap-2">
              {editingModel && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 p-0 sm:text-text-color sm:hover:bg-gray-200"
                  onClick={() => {
                    setModelToDelete({ index: editingModel.index, name: modelDialogValue });
                    setDeleteModelAlertOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <DialogClose className="flex-shrink-0" tabIndex={-1}>
                <X className="-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          <div className="px-4 pb-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="model-name" className="text-sm font-medium text-gray-700">
                {productForm.offeringType === 1 ? 'Model Name' : 'Package Name'}
              </Label>
              <Input
                id="model-name"
                value={modelDialogValue}
                onChange={(e) => setModelDialogValue(e.target.value)}
                placeholder={productForm.offeringType === 1 ? "e.g. Gold" : "e.g. Basic"}
                className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200"
                autoComplete="off"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && modelDialogValue.trim()) {
                    if (editingModel) {
                      const newModels = [...productForm.models];
                      newModels[editingModel.index].MNm = modelDialogValue.trim();
                      setProductForm(prev => ({ ...prev, models: newModels }));
                      // toast.success(`${productForm.offeringType === 1 ? 'Model' : 'Package'} updated.`);
                    } else {
                      const newModel = {
                        MID: productForm.models.length > 0 ? Math.max(...productForm.models.map(m => m.MID)) + 1 : 1,
                        MNm: modelDialogValue.trim()
                      };
                      setProductForm(prev => ({ ...prev, models: [...prev.models, newModel] }));
                      // toast.success(`${productForm.offeringType === 1 ? 'Model' : 'Package'} added.`);
                    }
                    setModelDialogOpen(false);
                    setEditingModel(null);
                    setModelDialogValue('');
                  }
                }}
              />
            </div>
            <div className="flex sm:flex-row gap-3">
              <Button
                onClick={() => {
                  if (!modelDialogValue.trim()) {
                    toast.error(`Please enter a ${productForm.offeringType === 1 ? 'model' : 'package'} name.`);
                    return;
                  }
                  if (editingModel) {
                    const newModels = [...productForm.models];
                    newModels[editingModel.index].MNm = modelDialogValue.trim();
                    setProductForm(prev => ({ ...prev, models: newModels }));
                    // toast.success(`${productForm.offeringType === 1 ? 'Model' : 'Package'} updated.`);
                  } else {
                    const newModel = {
                      MID: productForm.models.length > 0 ? Math.max(...productForm.models.map(m => m.MID)) + 1 : 1,
                      MNm: modelDialogValue.trim()
                    };
                    setProductForm(prev => ({ ...prev, models: [...prev.models, newModel] }));
                    // toast.success(`${productForm.offeringType === 1 ? 'Model' : 'Package'} added.`);
                  }
                  setModelDialogOpen(false);
                  setEditingModel(null);
                  setModelDialogValue('');
                }}
                className="sahi-primary-button sm:flex-none sm:min-w-[140px]"
              >
                <Check className="h-4 w-4" strokeWidth={3} />
                {editingModel ? `Update ${productForm.offeringType === 1 ? 'Model' : 'Package'}` : `Save ${productForm.offeringType === 1 ? 'Model' : 'Package'}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setModelDialogOpen(false);
                  setEditingModel(null);
                  setModelDialogValue('');
                }}
                className="sahi-secondary-button sm:flex-none sm:min-w-[100px]"
              >
                <X className="w-3 h-3" />
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Delete Confirmation AlertBox */}
      <AlertBox
        open={deleteAlertOpen}
        onOpenChange={setDeleteAlertOpen}
        title={`Delete ${deleteTarget.rid ? config.singularTitle :
          deleteTarget.fid ? config.singularTitle :
            deleteTarget.pid && showEntryMode ? config.singularTitle :
              deleteTarget.pid ? config.singularTitle :
                deleteTarget.index !== null ? config.singularTitle :
                  'Item'
          }?`}
        description={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
        actions={[
          {
            label: 'Close',
            value: 'cancel',
            variant: 'cancel',
            className: 'sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]',
            icon: <X className="w-3 h-3" />
          },
          {
            label: `Delete ${deleteTarget.rid ? config.singularTitle :
              deleteTarget.pid || deleteTarget.fid ? config.singularTitle :
                deleteTarget.index !== null ? config.singularTitle :
                  'Item'
              }`,
            value: 'delete',
            variant: 'action',
            className: 'sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]',
            icon: <Trash2 className="h-4 w-4" />
          },
        ]}
        onAction={(action) => {
          if (action === 'delete') {
            if (deleteTarget.rid) {
              handleFailuareReasonSave('delete', deleteTarget.rid);
            } else if (deleteTarget.fid) {  // ADD THIS
              handleThreadExtraFieldSave('delete', deleteTarget.fid);
            } else if (deleteTarget.pid && showEntryMode && isEditingProduct) {
              handleDeleteProduct();
              setShowEntryMode(false);
              setIsEditingProduct(false);
              setEditingProductID(null);
            } else if (deleteTarget.pid) {
              handleDeleteProduct();
            } else if (deleteTarget.index !== null) {
              if (isSegmentField && deleteTarget.segment) {
                handleMasterConfigSave('delete', deleteTarget.segment);
              } else {
                setMasterVal(deleteTarget.name);
                handleMasterConfigSave('delete', deleteTarget.name);
              }
            }
          } else {
            setDeleteAlertOpen(false);
            setDeleteTarget({ index: null, pid: null, name: '', rid: null, fid: null });  // ADD fid
          }
        }}
      />
      {/* Model/Package Delete Confirmation - Integrated with Unified AlertBox */}
      <AlertBox
        open={deleteModelAlertOpen}
        onOpenChange={setDeleteModelAlertOpen}
        title={`Delete ${productForm.offeringType === 1 ? 'Model' : 'Package'}?`}
        description={`Are you sure you want to delete "${modelToDelete?.name}"? This action cannot be undone.`}
        actions={[
          {
            label: 'Close',
            value: 'cancel',
            variant: 'cancel',
            className: 'sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]',
            icon: <X className="w-3 h-3" />
          },
          {
            label: `Delete ${productForm.offeringType === 1 ? 'Model' : 'Package'}`,
            value: 'delete',
            variant: 'action',
            className: 'sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]',
            icon: <Trash2 className="h-4 w-4" />
          },
        ]}
        onAction={(action) => {
          if (action === 'delete') {
            handleDeleteModel();
          } else {
            setDeleteModelAlertOpen(false);
            setModelToDelete(null);
          }
        }}
      />
      <ThrFieldConfig
        open={productConfigOpen}
        onClose={() => {
          setProductConfigOpen(false);
          setConfigThreadType(null);
        }}
        threadType={configThreadType}
        existingFields={[]} // Pass existing fields from your data
      />
    </>
  );
};



export default MastersDialog;